import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";
import { ArrowRight, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  // Show a friendly confirmation panel once submitted regardless of whether
  // the email exists — the backend never confirms account existence, and the
  // UI must not either.
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/password/forgot", {
        email: email.trim().toLowerCase(),
        origin: window.location.origin,
      });
      setSent(true);
    } catch {
      // Backend never surfaces errors here on purpose. If the network fails
      // we still show the same success state to keep behaviour consistent.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title="Forgot password — SerpNex" description="Reset your SerpNex password with a secure email link." canonical="/forgot-password" />
      <div className="min-h-screen grid md:grid-cols-2 bg-white">
        <div className="hidden md:flex flex-col justify-between p-14 bg-[#0A0D14] text-white relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-30" style={{ filter: 'invert(1)' }} />
          <Logo variant="dark" size="lg" />
          <div className="relative">
            <div className="eyebrow" style={{ color: '#7EA1FF' }}>Password reset</div>
            <h1 className="mt-3 h-display text-4xl sm:text-5xl font-bold">Locked out?<br />No drama.</h1>
            <p className="mt-6 text-white/60 max-w-md">Enter the email you signed up with. If we know it, a secure reset link arrives within a minute — links expire in 30 minutes for your safety.</p>
          </div>
          <div className="text-xs text-white/40 relative">One-time link · Expires in 30 min · Never shared</div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-14">
          {sent ? (
            <div className="w-full max-w-sm" data-testid="forgot-sent-panel">
              <div className="md:hidden mb-8"><Logo /></div>
              <div className="h-12 w-12 rounded-full bg-[#0047FF]/10 flex items-center justify-center mb-5">
                <Mail className="h-5 w-5 text-[#0047FF]" />
              </div>
              <h2 className="text-3xl font-display font-bold text-[#0A0D14]">Check your inbox.</h2>
              <p className="mt-3 text-sm text-[#525B6A]">
                If an account exists for <span className="text-[#0A0D14] font-medium">{email}</span>, a password reset link is on its way. Follow it within the next 30 minutes to pick a new password.
              </p>
              <p className="mt-6 text-sm text-[#525B6A]">
                Didn&apos;t receive anything? Check your spam folder or{" "}
                <button type="button" onClick={() => setSent(false)} data-testid="forgot-try-again" className="text-[#0047FF] font-medium hover:underline">try a different email</button>.
              </p>
              <div className="mt-8">
                <Link to="/login" data-testid="forgot-back-to-login" className="text-sm text-[#0047FF] font-medium">← Back to log in</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="w-full max-w-sm" data-testid="forgot-form">
              <div className="md:hidden mb-8"><Logo /></div>
              <h2 className="text-3xl font-display font-bold text-[#0A0D14]">Forgot password.</h2>
              <p className="mt-2 text-sm text-[#525B6A]">
                Remembered it? <Link to="/login" className="text-[#0047FF] font-medium">Log in instead</Link>.
              </p>
              <div className="mt-8 space-y-4">
                <div>
                  <label className="text-xs font-medium text-[#525B6A] mb-1 block">Email</label>
                  <input
                    required
                    type="email"
                    data-testid="forgot-email"
                    className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                </div>
                <button
                  disabled={busy}
                  data-testid="forgot-submit"
                  className="btn-primary w-full px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busy ? "Sending…" : (<>Send reset link <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
